import {Business} from "../../api/entities/Business";
import {
    EuiButton,
    EuiFieldText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFormRow,
    EuiLink,
    EuiSkeletonText,
    EuiSpacer,
    EuiText,
    EuiTextArea,
    EuiTitle,
} from "@elastic/eui";
import {PlanDetailsCard} from "../../components/plans/PlanDetailsCard";
import {
    useGetBusinessPlansQuery,
    useGetBusinessQuery,
    useGetDefaultPaymentMethodQuery,
    useUpdateBusinessMutation,
} from "../../api/rtkQueryApi/BusinessesApi";
import React, {FC, useEffect, useState} from "react";
import {Plan} from "../../api/entities/Plan";
import {useNavigate, useParams} from "react-router-dom";
import {UpdatePaymentMethodModal} from "../../components/payments/UpdatePaymentMethodModal";
import {PaymentMethodCard} from "../../api/entities/PaymentMethodCard";
import {IsEmail, IsZipCode} from "../../utils/Validation";
import {setBusinessBuilderAction} from "slices/NewOrderCreateSlice";
import {useDispatch} from "react-redux";
import {AudienceSearcher} from "../../components/audience/AudienceSearcher";

export const BusinessSummary: FC = () => {
    const dispatch = useDispatch();
    const {businessId} = useParams();
    const getBusinessPlansQuery = useGetBusinessPlansQuery(
        {businessId: businessId ?? ""},
        {skip: !businessId},
    );
    const [focussed, setFocussed] = React.useState<string[]>([]);
    const [showAudienceSearcher, setShowAudienceSearcher] = React.useState<boolean>(false);
    const [business, setBusiness] = useState<Business>();
    const [plans, setPlans] = useState<Plan[]>([]);
    const navigate = useNavigate();
    const [showUpdatePaymentMethodModal, setShowUpdatePaymentMethodModal] =
        useState(false);
    const getDefaultPaymentMethodQuery = useGetDefaultPaymentMethodQuery(
        {businessId: businessId ?? ""},
        {skip: !businessId},
    );
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodCard>();

    const getBusinessQuery = useGetBusinessQuery(
        {businessId: businessId ?? ""},
        {skip: !businessId},
    );

    const [updateBusiness, updateBusinessRequest] = useUpdateBusinessMutation();

    useEffect(() => {
        if (!business) {
            return;
        }

        dispatch(setBusinessBuilderAction({...business, isCreated: false, overrideSetupFee: 0, isOverrideSetupFee: false}));
    }, [dispatch, business]);

    useEffect(() => {
        if (getBusinessPlansQuery.data) {
            setPlans(getBusinessPlansQuery.data);
        }
    }, [getBusinessPlansQuery.data]);

    useEffect(() => {
        if (getBusinessQuery.data) {
            setBusiness(getBusinessQuery.data);
        }
    }, [getBusinessQuery.data]);

    useEffect(() => {
        if (getDefaultPaymentMethodQuery.data?.last4) {
            setPaymentMethod(getDefaultPaymentMethodQuery.data);
        }
    }, [getDefaultPaymentMethodQuery.data]);

    const addFocussed = (field: string) => {
        if (!focussed.includes(field)) {
            setFocussed([...focussed, field]);
        }
    };

    const hasFocussed = (field: string) => {
        return focussed.includes(field);
    };

    const onDescriptionChange = (description: string) => {
        if (business) {
            setBusiness({...business, description});
        }
    };

    const onClientFirstNameChange = (clientFirstName: string) => {
        if (business) {
            setBusiness({...business, clientFirstName});
        }
    };

    const onClientLastNameChange = (clientLastName: string) => {
        if (business) {
            setBusiness({...business, clientLastName});
        }
    };

    const onEmailChange = (email: string) => {
        if (business) {
            setBusiness({...business, email});
        }
    };

    const onPhoneChange = (phone: string) => {
        if (business) {
            setBusiness({...business, phone});
        }
    };

    const onWebsiteChange = (website: string) => {
        if (business) {
            setBusiness({...business, website});
        }
    };

    const onCreativesUrlChange = (creativesUrl: string) => {
        if (business) {
            setBusiness({...business, creativesUrl});
        }
    };

    const onStreetChange = (street: string) => {
        if (business) {
            setBusiness({
                ...business,
                address: {...business.address, street},
            });
        }
    };

    const onCityChange = (city: string) => {
        if (business) {
            setBusiness({
                ...business,
                address: {...business.address, city},
            });
        }
    };

    const onStateChange = (state: string) => {
        if (business) {
            setBusiness({
                ...business,
                address: {...business.address, state},
            });
        }
    };

    const onZipChange = (zip: string) => {
        if (business) {
            setBusiness({
                ...business,
                address: {...business.address, zip},
            });
        }
    };

    const onNotesChange = (notes: string) => {
        if (business) {
            setBusiness({
                ...business,
                growthCoachNotes: notes,
            });
        }
    }

    const onAdCritterNotesChange = (notes: string) => {
        if (business) {
            setBusiness({
                ...business,
                adCritterNotes: notes,
            });
        }
    }

    const onSave = () => {
        if (business) {
            updateBusiness({id: business?.id ?? "", business: business});
        }
    };

    const onNewPlanClick = () => {
        if (!business) {
            return;
        }

        navigate("new/product");
    };

    const onBusinessNameChange = (name: string) => {
        if (business) {
            setBusiness({...business, name});
        }
    }

    const isValid =
        !!business &&
        !!business.clientFirstName &&
        !!business.clientLastName &&
        IsEmail(business.email) &&
        IsZipCode(business.address.zip);

    if (getBusinessQuery.isLoading) {
        return <EuiSkeletonText lines={5} size={'m'} isLoading={true}/>
    }

    return (
        <>
            {!getDefaultPaymentMethodQuery.isLoading && (
                <EuiFlexGroup>
                    {paymentMethod && (
                        <EuiFlexItem grow={false}>
                            <EuiText size={"s"} style={{marginTop: 8}}>
                                <p>Payment Method: xxxxxxxxxx{paymentMethod.last4}</p>
                            </EuiText>
                        </EuiFlexItem>
                    )}
                    <EuiFlexItem grow={false}>
                        <EuiButton
                            size={"m"}
                            onClick={() => {
                                setShowUpdatePaymentMethodModal(true);
                            }}
                        >
                            {paymentMethod ? "Update Payment Method" : "Add Payment Method"}
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            )}
            <EuiSpacer/>
            {showAudienceSearcher && <>
                <AudienceSearcher />
                <EuiSpacer/>
            </>}
            <EuiLink onClick={() => setShowAudienceSearcher(!showAudienceSearcher)}><EuiText size={'s'}>{showAudienceSearcher ? 'Hide' : 'Show'} Audience Search</EuiText></EuiLink>
            <EuiSpacer/>
            <EuiFlexGroup>
                <EuiFlexItem>
                    <>
                        <EuiTitle size="s">
                            <h3>Notes</h3>
                        </EuiTitle>
                        <EuiSpacer/>
                        <EuiFormRow label="Business Description">
                            <EuiTextArea
                                value={business?.description}
                                onChange={(e) => onDescriptionChange(e.target.value)}
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"Notes for Social Media"}>
                            <EuiTextArea
                                value={business?.growthCoachNotes}
                                onChange={(e) => onNotesChange(e.target.value)}
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"Notes for AdCritter"}>
                            <EuiTextArea
                                value={business?.adCritterNotes}
                                onChange={(e) => onAdCritterNotesChange(e.target.value)}
                            />
                        </EuiFormRow>
                    </>
                </EuiFlexItem>
                <EuiFlexItem>
                    <>
                        <EuiTitle size="s">
                            <h3>Details</h3>
                        </EuiTitle>
                        <EuiSpacer/>
                        <EuiFormRow
                            label="Business Name"
                            isInvalid={
                                hasFocussed("Business Name") && !business?.name
                            }
                            error={"Business Name is Required"}
                        >
                            <EuiFieldText
                                value={business?.name}
                                onChange={(e) => onBusinessNameChange(e.target.value)}
                                onFocus={() => addFocussed("Business Name")}
                            />
                        </EuiFormRow>
                        <EuiFormRow
                            label="Client First Name"
                            isInvalid={
                                hasFocussed("Client First Name") && !business?.clientFirstName
                            }
                            error={"Client First Name is Required"}
                        >
                            <EuiFieldText
                                value={business?.clientFirstName}
                                onChange={(e) => onClientFirstNameChange(e.target.value)}
                                onFocus={() => addFocussed("Client First Name")}
                            />
                        </EuiFormRow>
                        <EuiFormRow
                            label="Client Last Name"
                            isInvalid={
                                hasFocussed("Client Last Name") && !business?.clientLastName
                            }
                            error={"Client Last Name is Required"}
                        >
                            <EuiFieldText
                                value={business?.clientLastName}
                                onChange={(e) => onClientLastNameChange(e.target.value)}
                                onFocus={() => addFocussed("Client Last Name")}
                            />
                        </EuiFormRow>
                        <EuiFormRow
                            label={"Email"}
                            isInvalid={
                                hasFocussed("Email") && !IsEmail(business?.email ?? "")
                            }
                            error={"Email is Invalid"}
                        >
                            <EuiFieldText
                                value={business?.email}
                                onChange={(e) => onEmailChange(e.target.value)}
                                onFocus={() => addFocussed("Email")}
                                placeholder="Email"
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"Phone"}>
                            <EuiFieldText
                                value={business?.phone}
                                onChange={(e) => onPhoneChange(e.target.value)}
                                placeholder="Phone"
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"Website"}>
                            <EuiFieldText
                                value={business?.website}
                                onChange={(e) => onWebsiteChange(e.target.value)}
                                placeholder="Website"
                            />
                        </EuiFormRow>
                        <EuiFormRow
                            label={"URL for Display Ads"}
                            labelAppend={
                                <EuiLink href={business?.creativesUrl} target={"_blank"}>
                                    Go
                                </EuiLink>
                            }
                        >
                            <EuiFieldText
                                value={business?.creativesUrl}
                                onChange={(e) => onCreativesUrlChange(e.target.value)}
                                placeholder="Creatives Url"
                            />
                        </EuiFormRow>
                    </>
                </EuiFlexItem>
                <EuiFlexItem>
                    <>
                        <EuiTitle size="s">
                            <h3>Address</h3>
                        </EuiTitle>
                        <EuiSpacer/>
                        <EuiFormRow label={"Street"}>
                            <EuiFieldText
                                value={business?.address.street}
                                onChange={(e) => onStreetChange(e.target.value)}
                                placeholder="Street"
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"City"}>
                            <EuiFieldText
                                value={business?.address.city}
                                onChange={(e) => onCityChange(e.target.value)}
                                placeholder="City"
                            />
                        </EuiFormRow>
                        <EuiFormRow label={"State"}>
                            <EuiFieldText
                                value={business?.address.state}
                                onChange={(e) => onStateChange(e.target.value)}
                                placeholder="State"
                            />
                        </EuiFormRow>
                        <EuiFormRow
                            label={"Zip"}
                            isInvalid={
                                hasFocussed("Zip") && !IsZipCode(business?.address.zip ?? "")
                            }
                            error={"Invalid Zip Code"}
                        >
                            <EuiFieldText
                                value={business?.address.zip}
                                onChange={(e) => onZipChange(e.target.value)}
                                onFocus={() => addFocussed("Zip")}
                                placeholder="Zip"
                            />
                        </EuiFormRow>

                        <EuiFormRow>
                            <EuiFlexGroup>
                                <EuiFlexItem grow={false}>
                                    <EuiButton
                                        onClick={onSave}
                                        isDisabled={!isValid}
                                        isLoading={updateBusinessRequest.isLoading}
                                        iconType={'save'}
                                        fill
                                    >
                                        Update
                                    </EuiButton>
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        </EuiFormRow>
                    </>
                </EuiFlexItem>
                <EuiFlexItem>
                    <>
                        <EuiTitle size="s">
                            <h3>Products</h3>
                        </EuiTitle>
                        <EuiLink onClick={onNewPlanClick}>Purchase Products</EuiLink>
                        <EuiSpacer/>
                        {business &&
                            plans?.map((plan, i) => (
                                <EuiFormRow key={i} fullWidth>
                                    <PlanDetailsCard
                                        key={plan.id}
                                        business={business}
                                        plan={plan}
                                    />
                                </EuiFormRow>
                            ))}
                    </>
                </EuiFlexItem>
            </EuiFlexGroup>
            {showUpdatePaymentMethodModal && businessId && (
                <UpdatePaymentMethodModal
                    businessId={businessId}
                    onClose={() => setShowUpdatePaymentMethodModal(false)}
                    onPaymentMethodUpdated={() => setShowUpdatePaymentMethodModal(false)}
                />
            )}
        </>
    );
};